import { useCallback, useState } from "react";
import { SpreadsheetRowData } from "@components/spreadsheet/types";
import { getExpandableRowIds } from "@components/spreadsheet/spreadsheetUtils";

interface UseExpandCollapseAllResult {
    expandedIds: string[];
    setExpandedIds: (expandedIds: string[]) => void;
    /** Seed for SpreadsheetGrid's defaultExpandedIds — bulk override or defaults. */
    expandedIdsSeed: string[];
    isCollapsed: boolean;
    handleExpandAll: () => void;
    handleCollapseAll: () => void;
}

function areStringArraysEqual(a: string[], b: string[]): boolean {
    if (a === b) {
        return true;
    }
    if (a.length !== b.length) {
        return false;
    }
    return a.every((id, index) => id === b[index]);
}

/**
 * Shared expand/collapse-all state for SpreadsheetGrid-based report task bodies.
 *
 * Only user-driven expansion state is stored (`userExpandedIds`); `expandedIds`
 * is derived from it with `defaultExpandedIds` as the fallback, so it always
 * matches the grid's own synchronous expansion seed on the first paint of an
 * already-cached report — otherwise consumers reading it (e.g.
 * shouldHideExpandedRowValue) would briefly render stale values and the button
 * icon would flash the wrong state.
 *
 * `expandedIdsOverride` is set by the expand/collapse-all button and overrides
 * `defaultExpandedIds` as the grid's seed.
 *
 * When `defaultExpandedIds` content changes (report structure reloaded), user
 * state is reset during render —
 * https://react.dev/reference/react/useState#storing-information-from-previous-renders
 * — so the new defaults apply without an intermediate stale render. Same-content
 * array identity changes (e.g. P&L refetch) keep the prior reference and user
 * expand state so SpreadsheetGrid does not reset.
 */
export function useExpandCollapseAll(
    rows: SpreadsheetRowData[] | null,
    defaultExpandedIds: string[],
): UseExpandCollapseAllResult {
    const [userExpandedIds, setUserExpandedIds] = useState<string[] | null>(
        null,
    );
    const [expandedIdsOverride, setExpandedIdsOverride] = useState<
        string[] | null
    >(null);
    const [stableDefaultExpandedIds, setStableDefaultExpandedIds] =
        useState(defaultExpandedIds);

    if (!areStringArraysEqual(stableDefaultExpandedIds, defaultExpandedIds)) {
        setStableDefaultExpandedIds(defaultExpandedIds);
        setUserExpandedIds(null);
        setExpandedIdsOverride(null);
    }

    const handleExpandAll = useCallback(() => {
        const ids = rows ? [...getExpandableRowIds(rows)] : [];
        setExpandedIdsOverride(ids);
        setUserExpandedIds(ids);
    }, [rows]);

    const handleCollapseAll = useCallback(() => {
        setExpandedIdsOverride([]);
        setUserExpandedIds([]);
    }, []);

    const expandedIds = userExpandedIds ?? stableDefaultExpandedIds;

    return {
        expandedIds,
        setExpandedIds: setUserExpandedIds,
        expandedIdsSeed: expandedIdsOverride ?? stableDefaultExpandedIds,
        isCollapsed: expandedIds.length === 0,
        handleExpandAll,
        handleCollapseAll,
    };
}
